import { create } from 'zustand';
import type {
  AiMixAction,
  ChannelMacros,
  CompPresetId,
  LoudnessTarget,
  MixerMode,
  SaturationMode,
  SslEqBand,
  StereoWidthPreset,
  TrackMeters,
} from '@melodinai/types';
import { getEngine } from '../audio/engineBridge';

interface MixerStore {
  mode: MixerMode;
  inspectorOpen: boolean;
  inspectorWidth: number;
  fullscreen: boolean;
  loudnessTarget: LoudnessTarget;
  widthPreset: StereoWidthPreset;
  meters: Record<string, TrackMeters>;
  setMode: (mode: MixerMode) => void;
  setInspectorOpen: (open: boolean) => void;
  setInspectorWidth: (w: number) => void;
  toggleFullscreen: () => void;
  setLoudnessTarget: (t: LoudnessTarget) => void;
  setWidthPreset: (p: StereoWidthPreset) => void;
  setMeters: (meters: Record<string, TrackMeters>) => void;
  hydrateMeters: () => () => void;
  setVolume: (trackId: string, volume: number) => void;
  setPan: (trackId: string, pan: number) => void;
  setMute: (trackId: string, muted: boolean) => void;
  setSolo: (trackId: string, soloed: boolean) => void;
  setArmed: (trackId: string, armed: boolean) => void;
  setInputGain: (trackId: string, gain: number) => void;
  setPhaseInvert: (trackId: string, invert: boolean) => void;
  setLowCut: (trackId: string, hz: number) => void;
  setSend: (trackId: string, index: number, amount: number) => void;
  setSendPre: (trackId: string, pre: boolean) => void;
  ensureChannelStrip: (trackId: string) => void;
  ensureMasterBus: () => void;
  setFxParam: (trackId: string, fxType: string, key: string, value: unknown) => void;
  setEqBand: (trackId: string, bandId: SslEqBand['id'], partial: Partial<Pick<SslEqBand, 'freq' | 'gain' | 'Q'>>) => void;
  setMacro: (trackId: string, key: keyof ChannelMacros, value: number) => void;
  applyCompPreset: (trackId: string, presetId: CompPresetId) => void;
  setSaturationMode: (trackId: string, mode: SaturationMode) => void;
  applyAiMixAction: (trackId: string, action: AiMixAction) => void;
}

export const useMixerStore = create<MixerStore>((set, get) => ({
  mode: 'simple',
  inspectorOpen: true,
  inspectorWidth: 320,
  fullscreen: false,
  loudnessTarget: 'streaming',
  widthPreset: 'wide',
  meters: {},

  setMode: (mode) => set({ mode }),
  setInspectorOpen: (inspectorOpen) => set({ inspectorOpen }),
  setInspectorWidth: (inspectorWidth) => set({ inspectorWidth: Math.max(260, Math.min(420, inspectorWidth)) }),
  toggleFullscreen: () => set({ fullscreen: !get().fullscreen }),
  setLoudnessTarget: (loudnessTarget) => {
    set({ loudnessTarget });
    getEngine().setLoudnessTarget?.(loudnessTarget);
  },
  setWidthPreset: (widthPreset) => {
    set({ widthPreset });
    getEngine().setMasterWidthPreset?.(widthPreset);
  },
  setMeters: (meters) => set({ meters }),
  hydrateMeters: () => {
    const eng = getEngine();
    if (!eng.subscribeMeters) return () => {};
    return eng.subscribeMeters((meters) => set({ meters }));
  },

  setVolume: (trackId, volume) => getEngine().setVolume?.(trackId, volume),
  setPan: (trackId, pan) => getEngine().setPan?.(trackId, pan),
  setMute: (trackId, muted) => getEngine().setMute?.(trackId, muted),
  setSolo: (trackId, soloed) => getEngine().setSolo?.(trackId, soloed),
  setArmed: (trackId, armed) => getEngine().setArmed?.(trackId, armed),
  setInputGain: (trackId, gain) => getEngine().setInputGain?.(trackId, gain),
  setPhaseInvert: (trackId, invert) => getEngine().setPhaseInvert?.(trackId, invert),
  setLowCut: (trackId, hz) => getEngine().setLowCut?.(trackId, hz),
  setSend: (trackId, index, amount) => getEngine().setSend?.(trackId, index, amount),
  setSendPre: (trackId, pre) => getEngine().setSendPre?.(trackId, pre),
  ensureChannelStrip: (trackId) => getEngine().ensureChannelStrip?.(trackId),
  ensureMasterBus: () => getEngine().ensureMasterBus?.(),
  setFxParam: (trackId, fxType, key, value) => getEngine().setFxParam?.(trackId, fxType, key, value),
  setEqBand: (trackId, bandId, partial) => getEngine().setEqBand?.(trackId, bandId, partial),
  setMacro: (trackId, key, value) => getEngine().setMacro?.(trackId, key, value),
  applyCompPreset: (trackId, presetId) => getEngine().applyCompPreset?.(trackId, presetId),
  setSaturationMode: (trackId, mode) => getEngine().setSaturationMode?.(trackId, mode),
  applyAiMixAction: (trackId, action) => getEngine().applyAiMixAction?.(trackId, action),
}));
